/**
 * Language targeting form — campaign-scoped rule-set editor.
 *
 * Users pick a campaign, toggle allow/deny mode, and select one or more
 * ISO-639-1 languages. The form wraps TanStack Query (load + save), React
 * Hook Form (state + validation), Zod v4 (schema) and Sonner (toasts).
 *
 * Visual language matches the Trackerity dash brief:
 *   - light theme, Inter, 6-8px rounds, zinc borders
 *   - emerald -> sky gradient heading at the top of the page layout
 *   - subtle shadows, no emoji, no competitor names
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import {
	LANGUAGE_OPTIONS,
	LANGUAGE_CODE_SET,
	TARGETING_MODES,
	fetchLanguageTargeting,
	languageTargetingQueryKey,
	resolveLanguageOption,
	upsertLanguageTargeting,
	type LanguageTargetingView,
} from "./api";

// ---------------------------------------------------------------------------
// Schema — mirrors the API's validation contract
// ---------------------------------------------------------------------------

const formSchema = z.object({
	campaignId: z
		.string()
		.trim()
		.min(1, "Campaign ID is required")
		.max(128, "Campaign ID is too long"),
	mode: z.enum(TARGETING_MODES),
	languages: z
		.array(z.string().regex(/^[a-z]{2}$/, "ISO-639-1 codes only"))
		.min(1, "Pick at least one language")
		.max(200, "Max 200 languages per campaign"),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	campaignId: "",
	mode: "allow",
	languages: [],
};

// ---------------------------------------------------------------------------
// Shared Tailwind atoms — keep consistent with the dash design tokens
// ---------------------------------------------------------------------------

const inputClass =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 shadow-[0_1px_2px_0_rgb(24_24_27_/0.04)] focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-zinc-50 disabled:text-zinc-400";

const labelClass = "block text-sm font-medium text-zinc-700";
const hintClass = "mt-1 text-xs text-zinc-500";
const errorClass = "mt-1 text-xs text-red-600";

// ---------------------------------------------------------------------------
// Form component
// ---------------------------------------------------------------------------

export function LanguageTargetingForm() {
	const queryClient = useQueryClient();

	// Local state for the campaign id users are currently loading rules for.
	// Kept separate from the form's campaignId so users can edit the input
	// without triggering a refetch on every keystroke.
	const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);

	const {
		control,
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors, isDirty, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: DEFAULT_VALUES,
		mode: "onBlur",
	});

	const currentMode = watch("mode");
	const currentLanguages = watch("languages");
	const currentCampaignId = watch("campaignId");

	// -------------------------------------------------------------------
	// Load existing rule set when the user confirms a campaign id
	// -------------------------------------------------------------------
	const loadQuery = useQuery({
		queryKey: languageTargetingQueryKey(activeCampaignId ?? ""),
		queryFn: () => fetchLanguageTargeting(activeCampaignId as string),
		enabled: activeCampaignId !== null && activeCampaignId.length > 0,
		staleTime: 15_000,
	});

	useEffect(() => {
		if (!activeCampaignId) return;
		if (loadQuery.isSuccess) {
			const data: LanguageTargetingView | null | undefined = loadQuery.data;
			if (data) {
				reset({
					campaignId: activeCampaignId,
					mode: data.mode,
					languages: data.languages.filter((c) => /^[a-z]{2}$/.test(c)),
				});
				toast.success("Loaded existing rule set", {
					description: `${data.languages.length} language${data.languages.length === 1 ? "" : "s"} in ${data.mode} mode`,
				});
			} else {
				reset({ ...DEFAULT_VALUES, campaignId: activeCampaignId });
				toast.message("No rule set yet", {
					description: "Pick languages and save to create one.",
				});
			}
		}
	}, [
		loadQuery.isSuccess,
		loadQuery.data,
		activeCampaignId,
		reset,
	]);

	useEffect(() => {
		if (loadQuery.isError) {
			toast.error("Failed to load rule set", {
				description:
					loadQuery.error instanceof Error ? loadQuery.error.message : "Unknown error",
			});
		}
	}, [loadQuery.isError, loadQuery.error]);

	// -------------------------------------------------------------------
	// Save mutation
	// -------------------------------------------------------------------
	const saveMutation = useMutation({
		mutationFn: upsertLanguageTargeting,
		onSuccess: (saved) => {
			queryClient.setQueryData(languageTargetingQueryKey(saved.campaign_id), saved);
			toast.success("Rule set saved", {
				description: `${saved.languages.length} language${saved.languages.length === 1 ? "" : "s"} in ${saved.mode} mode`,
			});
			reset({
				campaignId: saved.campaign_id,
				mode: saved.mode,
				languages: saved.languages,
			});
			setActiveCampaignId(saved.campaign_id);
		},
		onError: (err) => {
			toast.error("Save failed", {
				description: err instanceof Error ? err.message : "Unknown error",
			});
		},
	});

	const onSubmit: SubmitHandler<FormValues> = (values) => {
		saveMutation.mutate(values);
	};

	const handleLoadClick = () => {
		const id = currentCampaignId?.trim();
		if (!id) {
			toast.error("Enter a campaign ID first");
			return;
		}
		setActiveCampaignId(id);
	};

	const toggleLanguage = (code: string) => {
		const next = new Set(currentLanguages ?? []);
		if (next.has(code)) next.delete(code);
		else next.add(code);
		setValue("languages", Array.from(next), { shouldDirty: true, shouldValidate: true });
	};

	const clearAll = () => {
		setValue("languages", [], { shouldDirty: true, shouldValidate: true });
	};

	const selectAll = () => {
		setValue(
			"languages",
			LANGUAGE_OPTIONS.map((o) => o.code),
			{ shouldDirty: true, shouldValidate: true },
		);
	};

	const selectedCount = currentLanguages?.length ?? 0;
	const isLoading = loadQuery.isFetching;

	const summary = useMemo(() => {
		if (!currentLanguages || currentLanguages.length === 0) return null;
		return currentLanguages
			.slice()
			.sort()
			.map((c) => resolveLanguageOption(c).label)
			.join(", ");
	}, [currentLanguages]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="flex flex-col gap-6"
			noValidate
		>
			{/* Campaign ID row */}
			<section className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[0_1px_3px_0_rgb(24_24_27_/0.06)]">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-end">
					<div className="flex-1">
						<label htmlFor="campaignId" className={labelClass}>
							Campaign ID
						</label>
						<input
							id="campaignId"
							type="text"
							autoComplete="off"
							placeholder="camp_01HX..."
							className={inputClass}
							{...register("campaignId")}
						/>
						{errors.campaignId ? (
							<p className={errorClass}>{errors.campaignId.message}</p>
						) : (
							<p className={hintClass}>
								Load existing rules, or save to create a new rule set.
							</p>
						)}
					</div>
					<button
						type="button"
						onClick={handleLoadClick}
						disabled={isLoading}
						className="inline-flex h-10 items-center justify-center rounded-[6px] border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 shadow-[0_1px_2px_0_rgb(24_24_27_/0.04)] transition hover:border-zinc-300 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{isLoading ? "Loading..." : "Load rules"}
					</button>
				</div>
			</section>

			{/* Mode toggle */}
			<section className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[0_1px_3px_0_rgb(24_24_27_/0.06)]">
				<div className={labelClass}>Targeting mode</div>
				<p className={hintClass}>
					Allow lets only the selected languages through. Deny blocks the
					selected languages and lets everything else through.
				</p>
				<Controller
					control={control}
					name="mode"
					render={({ field }) => (
						<div
							role="radiogroup"
							aria-label="Targeting mode"
							className="mt-3 inline-flex rounded-[8px] border border-zinc-200 bg-zinc-50 p-1"
						>
							{TARGETING_MODES.map((m) => {
								const active = field.value === m;
								return (
									<button
										key={m}
										type="button"
										role="radio"
										aria-checked={active}
										onClick={() => field.onChange(m)}
										className={
											"min-w-[96px] rounded-[6px] px-3 py-1.5 text-sm font-medium transition " +
											(active
												? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27_/0.08)]"
												: "text-zinc-500 hover:text-zinc-700")
										}
									>
										{m === "allow" ? "Allow" : "Deny"}
									</button>
								);
							})}
						</div>
					)}
				/>
				{errors.mode && <p className={errorClass}>{errors.mode.message}</p>}
			</section>

			{/* Language multi-select combobox */}
			<section className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[0_1px_3px_0_rgb(24_24_27_/0.06)]">
				<div className="flex items-center justify-between gap-3">
					<div>
						<div className={labelClass}>Languages</div>
						<p className={hintClass}>
							Select the ISO-639-1 languages for this {currentMode} list.
						</p>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={selectAll}
							className="rounded-[6px] px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
						>
							Select all
						</button>
						<button
							type="button"
							onClick={clearAll}
							className="rounded-[6px] px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
						>
							Clear
						</button>
					</div>
				</div>

				<LanguageCombobox
					selected={currentLanguages ?? []}
					onToggle={toggleLanguage}
				/>

				<div className="mt-4 flex items-center justify-between gap-3 text-xs text-zinc-500">
					<span>
						{selectedCount === 0
							? "No languages selected."
							: `${selectedCount} selected${summary ? ": " + summary : ""}`}
					</span>
				</div>

				{errors.languages && (
					<p className={errorClass}>{errors.languages.message}</p>
				)}
			</section>

			{/* Submit row */}
			<div className="flex items-center justify-end gap-3">
				<span className="text-xs text-zinc-400">
					{isDirty ? "Unsaved changes" : "No changes"}
				</span>
				<button
					type="submit"
					disabled={isSubmitting || saveMutation.isPending}
					className="inline-flex h-10 items-center justify-center rounded-[6px] bg-zinc-900 px-5 text-sm font-semibold text-white shadow-[0_1px_3px_0_rgb(24_24_27_/0.2)] transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{saveMutation.isPending ? "Saving..." : "Save rule set"}
				</button>
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Inline combobox — simple controlled filter over the ISO-639-1 options
// ---------------------------------------------------------------------------

interface LanguageComboboxProps {
	selected: string[];
	onToggle: (code: string) => void;
}

function LanguageCombobox({ selected, onToggle }: LanguageComboboxProps) {
	const [query, setQuery] = useState("");

	const selectedSet = useMemo(() => new Set(selected), [selected]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return LANGUAGE_OPTIONS;
		return LANGUAGE_OPTIONS.filter((opt) => {
			return (
				opt.code.includes(q) ||
				opt.label.toLowerCase().includes(q) ||
				opt.native.toLowerCase().includes(q)
			);
		});
	}, [query]);

	return (
		<div className="mt-3 flex flex-col gap-3">
			<input
				type="text"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder="Filter languages..."
				className={inputClass}
				aria-label="Filter languages"
			/>

			{selected.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{selected.map((code) => {
						const opt = resolveLanguageOption(code);
						return (
							<span
								key={code}
								className="inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-700"
							>
								<span className="font-mono text-zinc-500">{opt.code}</span>
								<span>{opt.label}</span>
								<button
									type="button"
									onClick={() => onToggle(code)}
									aria-label={`Remove ${opt.label}`}
									className="rounded-full px-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700"
								>
									x
								</button>
							</span>
						);
					})}
				</div>
			)}

			<ul
				role="listbox"
				aria-label="Available languages"
				aria-multiselectable="true"
				className="grid max-h-80 grid-cols-1 gap-1 overflow-auto rounded-[6px] border border-zinc-200 p-1 sm:grid-cols-2"
			>
				{filtered.length === 0 && (
					<li className="p-3 text-center text-xs text-zinc-400">
						No languages match "{query}".
					</li>
				)}
				{filtered.map((opt) => {
					const active = selectedSet.has(opt.code);
					const known = LANGUAGE_CODE_SET.has(opt.code);
					return (
						<li key={opt.code} role="option" aria-selected={active}>
							<button
								type="button"
								onClick={() => onToggle(opt.code)}
								className={
									"flex w-full items-center justify-between gap-3 rounded-[6px] px-3 py-2 text-left text-sm transition " +
									(active
										? "border border-sky-200 bg-sky-50 text-zinc-900"
										: "border border-transparent hover:bg-zinc-50")
								}
							>
								<div className="flex min-w-0 items-center gap-3">
									<span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border border-zinc-200 bg-white font-mono text-[11px] uppercase text-zinc-600">
										{opt.code}
									</span>
									<div className="min-w-0">
										<div className="truncate font-medium text-zinc-800">
											{opt.label}
										</div>
										<div className="truncate text-xs text-zinc-500">
											{opt.native}
										</div>
									</div>
								</div>
								<span
									aria-hidden="true"
									className={
										"inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border text-xs " +
										(active
											? "border-sky-400 bg-sky-500 text-white"
											: known
												? "border-zinc-300 bg-white text-transparent"
												: "border-zinc-200 bg-white text-transparent")
									}
								>
									{active ? "Y" : ""}
								</span>
							</button>
						</li>
					);
				})}
			</ul>
		</div>
	);
}
